/**
 * AdSpendCurrency — the unit every ad-money figure in this workspace counts
 * in, at `workspaces/{orgId}/settings/adSpendCurrency`, beside the ceiling it
 * denominates.
 *
 * Two operators must see the unit BEFORE they type a figure into a field
 * denominated in it: the one committing a budget (`tickets.ads.manage`) and
 * the one setting the bound that budget is refused against
 * (`tickets.events.manage`). That pair is why the value sits on neither
 * surface's own document. `settings/private` is read behind
 * `tickets.events.manage` alone, so the launcher never reaches it.
 * `settings/adSpendCeiling` takes a client write carrying no key mask, so the
 * write moving the bound is free to drop a field written beside it — and its
 * own figure is required precisely so an absent DOCUMENT means an absent
 * bound, the meaning a provider-written field merged onto a workspace that
 * has set no bound yet would take away.
 *
 * The read admits both tokens, ORed, rather than handing either holder the
 * other's grant. Both grants cost more than they buy: an ads token that also
 * read `settings/private` would reach the Revolut operating config, the
 * wallet issuer ids and the invoice counter — the audience argument that
 * minted the ceiling its own document; an events token that also carried
 * `tickets.ads.manage` could raise the ceiling AND spend against it,
 * collapsing that separation from the other side. A union widens who sees one
 * string; a grant widens what a role is. Neither holder learns anything new
 * — the administrator already reads the ad account this unit belongs to, and
 * the spender already reads the bound counted in it — which is the same shape
 * member.entity.ts's own ORed read carries: a role granted one of the pair
 * without the other must not silently lose a value its surface needs.
 *
 * No write action is declared, so every client write path is default-denied,
 * the shape door-labels.entity.ts and integration.entity.ts already carry.
 * Not `.requiresHandler(...)`: that marker names the callable a CLIENT
 * invokes to perform a verb, and no client asks for this value. It is
 * observed, not requested — the save that stores the ad account reads the
 * account's currency in the same act and merges it here under the Admin SDK,
 * which rules do not reach. That save already holds the answer, so nothing
 * here lags a save behind.
 *
 * Fields justified:
 *   currency — the ad account's own value as READ WHEN THAT ACCOUNT WAS LAST
 *           STORED: a stamped cache whose refresh point is that act, never a
 *           fact about the account now, so no branch may turn on a value SET.
 *           The vocabulary is Meta's and its Marketing API publishes no set
 *           for this field to transcribe — a closed set here would be
 *           asserted by analogy instead. Loose for the reason
 *           MetaAdvertiserSchema states besides: a value already at rest
 *           stays readable whatever Meta does to its own vocabulary. Nullish
 *           because the storing act is the only refresh — an account stored
 *           while its currency could not be read leaves this null rather than
 *           let a previous account's unit stand beside the new account's id.
 * Excluded:
 *   the ad account id it was read from — `settings/private`.metaAds owns it,
 *           and the reader who most needs the unit is denied that document, so
 *           a copy here would hand a token the identifier the deployment
 *           deliberately keeps from it, and be a second answer besides.
 *   when it was read — the refresh point is the storing act, and a stamp
 *           would invite exactly the staleness branch the field above forbids.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

export const AdSpendCurrencySchema = z.object({
	currency: z
		.string()
		.min(1)
		.nullish()
		.describe(
			"the ad account's own currency as read when that account was last stored — the unit this workspace's ad budgets, spend figures and ceiling all count in",
		),
});
export type AdSpendCurrency = z.infer<typeof AdSpendCurrencySchema>;

export const AdSpendCurrency = entity<Actor>()({
	schema: AdSpendCurrencySchema,
	actions: ["read"] as const,
})
	.pipe(({ can, hasPermission, and, or }) => [
		// Both surfaces that denominate a money field in this unit, and nothing
		// wider: the one that commits the budget, and the one that bounds it.
		can("read").when((_data, me) =>
			and(
				or(
					hasPermission(me.permissions, "tickets.ads.manage"),
					hasPermission(me.permissions, "tickets.events.manage"),
				),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/settings/adSpendCurrency",
		}),
	);
